import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Phone, Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { SITE, EASE } from "@/lib/site";
import { SERVICES } from "@/lib/services";
import { useGoTo } from "@/hooks/useGoTo";

const ServicesMenu = ({ solid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="nav-services-dropdown"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 py-6 text-sm font-medium"
        aria-expanded={open}
      >
        Nos Services <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="services-dropdown-panel"
            initial={{ opacity: 0, y: 12, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="absolute left-1/2 top-full w-[620px] -translate-x-1/2 border border-paper-line bg-paper p-3 text-ink shadow-[0_30px_80px_-30px_rgba(17,22,28,0.45)]"
          >
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`dropdown-service-${s.slug}`}
                onClick={() => setOpen(false)}
                className="group flex items-center gap-5 p-3 transition-colors duration-300 hover:bg-paper-deep"
              >
                <span className="flex h-16 w-20 shrink-0 items-center justify-center gap-1 bg-white">
                  <img src={s.icon} alt="" className="h-12 w-auto transition-transform duration-500 group-hover:scale-110" />
                </span>
                <span className="flex-1">
                  <span className="font-mono text-[11px] text-gold-deep">{s.num}</span>
                  <span className="block font-display text-xl leading-tight">{s.title}</span>
                  <span className="block text-xs text-slate-600 mt-1 line-clamp-1">{s.short}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 opacity-0 -translate-x-2 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ goTo }) => {
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); goTo(id); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden p-2 -mr-2">
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" aria-describedby={undefined} className="w-full max-w-sm bg-ink text-paper border-ink-line p-8">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <nav className="mt-10 flex flex-col gap-6" data-testid="mobile-menu">
          <Link to="/" onClick={() => setOpen(false)} className="font-display text-4xl" data-testid="mobile-nav-home">Accueil</Link>
          <span className="eyebrow text-gold mt-4">Nos services</span>
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-service-${s.slug}`} className="font-display text-2xl leading-tight text-paper/90">
              {s.title}
            </Link>
          ))}
          <button onClick={() => go("realisations")} className="text-left font-display text-2xl mt-4" data-testid="mobile-nav-realisations">Réalisations</button>
          <button onClick={() => go("galerie")} className="text-left font-display text-2xl" data-testid="mobile-nav-galerie">Galerie photos</button>
          <button onClick={() => go("avis")} className="text-left font-display text-2xl" data-testid="mobile-nav-avis">Avis clients</button>
          <a href={SITE.phoneHref} className="mt-6 flex items-center gap-3 text-gold text-xl" data-testid="mobile-nav-phone"><Phone className="h-5 w-5" /> {SITE.phone}</a>
          <Link to="/devis" onClick={() => setOpen(false)} data-testid="mobile-nav-devis" className="mt-2 bg-gold px-6 py-4 text-center font-semibold text-ink">Devis Gratuit</Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const { pathname } = useLocation();
  const goTo = useGoTo();
  const [scrolled, setScrolled] = useState(false);
  const darkHero = pathname === "/" || pathname.startsWith("/services");

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const solid = scrolled || !darkHero;
  return (
    <motion.header
      data-testid="main-header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,color,border-color] duration-500 border-b ${
        solid ? "bg-paper/85 backdrop-blur-xl text-ink border-paper-line" : "bg-transparent text-paper border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 sm:px-8 py-2">
        <Link to="/" data-testid="header-logo-link"><Logo light={!solid} compact={scrolled} /></Link>
        <nav className="hidden lg:flex items-center gap-9">
          <Link to="/" className="py-6 text-sm font-medium link-line" data-testid="nav-home">Accueil</Link>
          <ServicesMenu solid={solid} />
          <button onClick={() => goTo("realisations")} className="py-6 text-sm font-medium link-line" data-testid="nav-realisations">Réalisations</button>
          <button onClick={() => goTo("galerie")} className="py-6 text-sm font-medium link-line" data-testid="nav-galerie">Galerie</button>
          <button onClick={() => goTo("avis")} className="py-6 text-sm font-medium link-line" data-testid="nav-avis">Avis</button>
        </nav>
        <div className="flex items-center gap-3 sm:gap-5">
          <a href={SITE.phoneHref} data-testid="header-phone-link" className="hidden md:flex items-center gap-2.5">
            <span className={`flex h-9 w-9 items-center justify-center rounded-full border ${solid ? "border-ink/20" : "border-paper/30"}`}>
              <Phone className="h-4 w-4" />
            </span>
            <span className="leading-tight">
              <span className={`block text-[10px] font-mono uppercase tracking-[0.2em] ${solid ? "text-slate-600" : "text-paper/75"}`}>Appelez-nous</span>
              <span className="block text-[15px] font-semibold tracking-wide">{SITE.phone}</span>
            </span>
          </a>
          <a href={SITE.phoneHref} data-testid="header-phone-icon-mobile" aria-label="Appeler" className="md:hidden p-2">
            <Phone className="h-5 w-5" />
          </a>
          <Link
            to="/devis"
            data-testid="header-devis-button"
            className="group relative hidden sm:inline-flex items-center gap-2 overflow-hidden bg-gold px-5 py-3 text-sm font-semibold text-ink"
          >
            <span className="absolute inset-0 -translate-x-full bg-paper transition-transform duration-500 ease-out group-hover:translate-x-0" />
            <span className="relative">Devis Gratuit</span>
            <ArrowUpRight className="relative h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
          </Link>
          <MobileMenu goTo={goTo} />
        </div>
      </div>
    </motion.header>
  );
};
